import { BILLING_PRODUCT } from "../services/billing/billing.const"
import { getAddonFreeQuota, getAddonStripe, getAddonTier, getAddonUsage, isAddonFreeTier } from "./addon-plan"
import { isOrgEnterprise } from "./org-plan"

/**
 * The org's AI Purchasing Stripe state for the account this build talks to. The API keeps
 * live-account state in purchasing.stripe and test-account state in purchasing.stripeTest
 * (localhost runs the production DB against Stripe's test account, and ids from one
 * account mean nothing to the other), choosing by its secret key. This chooses by the
 * matching publishable key, so both sides always read the same slot. Every read of
 * add-on subscription state on the client goes through here - never through
 * orgData.purchasing.stripe directly.
 * @param {object} orgData
 * @returns {object} The stripe sub-document for this mode, or {} when there is none.
 */
export const getPurchasingStripe = (orgData) => getAddonStripe(orgData, BILLING_PRODUCT.PURCHASING)

/**
 * The AI Purchasing tier an org is effectively on, resolved the same way
 * AiPurchasingPricing/UsageBreakdown decide whether the add-on is "live": a live
 * Stripe subscription (active/trialing/past_due) with a known tier wins, anything else
 * is the Free allowance bundled with the paid plan. Display-only, like billing.const -
 * the API decides what actually gets billed/enforced.
 * @param {object} orgData
 * @returns {object} A PURCHASING_TIERS entry, or PURCHASING_FREE_TIER
 */
export const getPurchasingTier = (orgData) => getAddonTier(orgData, BILLING_PRODUCT.PURCHASING)

export const isPurchasingFreeTier = (orgData) => isAddonFreeTier(orgData, BILLING_PRODUCT.PURCHASING)

/**
 * The Free allowance of AI-processed invoices this org gets bundled with its paid plan -
 * smaller for AppSumo orgs. Use this rather than PURCHASING_FREE_TIER.includedQuota
 * anywhere the number is quoted at a specific org.
 * @param {object} orgData
 * @returns {number}
 */
export const getPurchasingFreeQuota = (orgData) => getAddonFreeQuota(orgData, BILLING_PRODUCT.PURCHASING)

/**
 * Whether the org may create invoices at all. An active paid licence (or Enterprise) is
 * the floor, whatever add-on tier it holds - buying AI Purchasing on its own is not
 * enough. Mirrors orgMiddleware.requiresActivePurchasingPlan in mission-x-api, which
 * refuses the write with `org_purchasing_unlicensed` regardless of what the UI shows.
 * When this is false Purchasing is read-only: everything already filed stays visible and
 * searchable, and every upload control is hidden rather than left to fail on submit.
 * @param {object} orgData
 * @returns {boolean}
 */
export const canUsePurchasing = (orgData) => Boolean(orgData?.isLicensedActive) || isOrgEnterprise(orgData)

/**
 * Automated approval workflows (org-level auto-approve + threshold rules, per-vendor
 * auto-issue/auto-approve) are a paid-tier feature - Free is manual approval only.
 * @param {object} orgData
 * @returns {boolean}
 */
export const canUsePurchasingWorkflows = (orgData) => !isPurchasingFreeTier(orgData)

/**
 * This month's AI-processed invoice usage against the tier's quota. `used` is the
 * Mongo-native counter the API bumps on every vendor invoice creation
 * (org.purchasing.usage.currentPeriodCount); `quota` prefers what the API stamped on
 * the org at subscription time and falls back to the tier config (Free has no
 * subscription, so nothing is stamped).
 * @param {object} orgData
 * @returns {{ tier: object, used: number, quota: number, remaining: number, overage: number }}
 */
export const getPurchasingUsage = (orgData) => getAddonUsage(orgData, BILLING_PRODUCT.PURCHASING)
